import React, { useState } from "react";
import { makeStyles, Button, Input, Text, Link } from "@fluentui/react-components";
import { ErrorCircle16Filled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant ConnexionPage.
 */
interface ConnexionProps {
  setCurrentView: (view: string) => void;
  seConnecter: (data: { email: string; password: string }) => Promise<{ message: string }>;
}

/**
 * Composant ConnexionPage : permet à l'utilisateur de se connecter en saisissant son email et son mot de passe.
 * @param {ConnexionProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const ConnexionPage: React.FC<ConnexionProps> = ({ setCurrentView, seConnecter }) => {
  const styles = useStyles();
  // États locaux pour gérer les entrées utilisateur et l'état du composant
  const [email, setEmail] = useState(""); // Adresse email de l'utilisateur
  const [passwords, setPasswords] = useState({ password: "", confirm: "" }); // Mot de passe de l'utilisateur
  const [errors, setErrors] = useState(""); // Message d'erreur en cas de problème
  const [loading, setLoading] = useState(false); // Indique si une requête est en cours
  const [success, setSuccess] = useState(false); // Indique si la connexion a réussi
  const [btnText, setBtnText] = useState("Se connecter"); // Texte du bouton de connexion

  /**
   * Valide les informations saisies (email et mot de passe) et tente de connecter l'utilisateur.
   */
  async function validatePasswordAndEmail() {
    setLoading(true);
    setBtnText("Veuillez patienter ...");
    setErrors("");
    try {
      await seConnecter({
        email,
        password: passwords.password,
      });
      setSuccess(true);
    } catch (error: any) {
      setErrors(error.message || "Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
      setBtnText("Se Connecter");
    }
  }

  /**
   * Vérifie si le bouton de connexion doit être désactivé.
   * @returns {boolean} - `true` si le bouton doit être désactivé, sinon `false`.
   */
  function buttonDisabled() {
    return passwords.password === "" || email === "" || loading;
  }

  // Rendu du composant
  return (
    <>
      <div data-testid="connexion-page" className={styles.container}>
        {/* Formulaire de connexion */}
        <form
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (!buttonDisabled()) {
                validatePasswordAndEmail();
              }
            }
          }}
        >
          {/* Adresse courriel */}
          <div>
            <label htmlFor="email" className="block text-xs text-gray-600 uppercase">
              Adresse courriel:
            </label>
            <Input
              name="email"
              id="email"
              type="email"
              data-testid="email-input"
              autoComplete="email"
              required
              className={styles.formField}
              placeholder="usager@example.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
            />
          </div>
          {/* Mot de passe */}
          <div>
            <label htmlFor="password" className="block text-xs text-gray-600 uppercase">
              Mot de passe:
            </label>
            <Input
              name="password"
              type="password"
              id="password"
              data-testid="password-input"
              autoComplete="password"
              className={styles.formField}
              placeholder="********"
              value={passwords.password}
              onChange={(e: any) => setPasswords({ ...passwords, password: e.target.value })}
            />
          </div>
          {/* Message d'erreur */}
          {errors && (
            <div className={styles.errorText}>
              <ErrorCircle16Filled style={{ marginRight: "8px" }} />
              <Text>{errors}</Text>
            </div>
          )}
          {/* Mot de passe oublié */}
          <Text>
            <Link onClick={() => setCurrentView("MotDePasseOublier")}>Mot de passe oublié?</Link>
          </Text>
        </form>
      </div>
      {/* Lien inscription */}
      <div className={styles.container}>
        <Text>
          Vous n'avez pas de compte?{" "}
          <Link data-testid="signup-button" onClick={() => setCurrentView("SignupPage")}>
            S'inscrire
          </Link>
        </Text>
        <br />
        {/* Bouton submit */}
        <Button
          appearance="primary"
          className={!buttonDisabled() ? styles.greenButton : undefined}
          data-testid="login-button"
          disabled={buttonDisabled()}
          onClick={() => validatePasswordAndEmail()}
        >
          {btnText}
        </Button>
      </div>
    </>
  );
};

export default ConnexionPage;

const useStyles = makeStyles({
  container: {
    display: "flex",
    width: "80%",
    padding: "4vh",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  },
  formField: {
    marginBottom: "12px",
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  heading: {
    margin: "0px 0px 16px 0px",
    width: "100%",
  },
  errorText: {
    color: "red",
    display: "flex",
    alignItems: "center",
  },
  successText: {
    color: "green",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
  greenButton: {
    backgroundColor: "#217346",
    width: "100%",
    margin: "5px 0",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
});
